import React, { useState, useEffect } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
const { FiUser, FiUploadCloud } = FiIcons;
import { useProfile } from '../context/ProfileContext';
import { supabase } from '../supabaseClient';
import { useAuth } from '../context/AuthContext';
import { callOpenAI } from '../openaiClient';
import * as pdfjsLib from 'pdfjs-dist';
import mammoth from 'mammoth';

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const Settings = () => {
  const { data, refreshData, loading } = useProfile();
  const { session } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    title: '',
    summary: '',
    skills: [],
    education: [],
    experience: [],
    publications: [],
    gpa: '',
    affiliations: [],
    certifications: [],
    organizations: [],
    github_url: '',
    portfolio_url: '',
    social_links: [
      { platform: 'LinkedIn', url: '' },
      { platform: 'GitHub', url: '' },
      { platform: 'Portfolio', url: '' }
    ]
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    if (data && data.user) {
      setFormData({
        name: data.user.name || '',
        title: data.user.title || '',
        summary: data.user.summary || '',
        skills: data.user.skills || [],
        education: data.user.education || [],
        experience: data.user.experience || [],
        publications: data.user.publications || [],
        gpa: data.user.gpa || '',
        affiliations: data.user.affiliations || [],
        certifications: data.user.certifications || [],
        organizations: data.user.organizations || [],
        github_url: data.user.github_url || '',
        portfolio_url: data.user.portfolio_url || '',
        social_links: data.user.social_links?.length ? data.user.social_links : [
          { platform: 'LinkedIn', url: '' },
          { platform: 'GitHub', url: '' },
          { platform: 'Portfolio', url: '' }
        ]
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSocialLinkChange = (index, field, value) => {
    setFormData(prev => {
      const newLinks = [...prev.social_links];
      newLinks[index] = { ...newLinks[index], [field]: value };
      return { ...prev, social_links: newLinks };
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setIsParsing(true);
    setMessage({ type: '', text: '' });

    try {
      let extractedText = '';

      if (file.type === 'application/pdf' || file.name.endsWith('.pdf')) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const strings = content.items.map(item => item.str);
          extractedText += strings.join(' ') + '\n';
        }
      } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer });
        extractedText = result.value;
      } else {
        throw new Error('Unsupported file format. Please upload a PDF or DOCX file.');
      }

      if (!extractedText.trim()) {
         throw new Error('Could not extract text from the document.');
      }

      const systemPrompt = `You are an expert resume parser. Extract the candidate's full name, professional title, a brief professional summary, skills, education, work experience, publications, GPA, affiliations, certifications, and organizations from the provided resume text.
Return ONLY a valid JSON object with the following structure:
{
  "name": "string",
  "title": "string",
  "summary": "string",
  "skills": ["skill1", "skill2"],
  "education": [
    {
      "school": "string",
      "degree": "string",
      "duration": "string"
    }
  ],
  "experience": [
    {
      "role": "string",
      "company": "string",
      "duration": "string",
      "bullets": ["bullet1", "bullet2"]
    }
  ],
  "publications": ["pub1", "pub2"],
  "gpa": "string",
  "affiliations": ["affil1", "affil2"],
  "certifications": ["cert1", "cert2"],
  "organizations": ["org1", "org2"]
}
If you cannot find a specific field, leave it as an empty string or empty array.`;

      let parsedData = await callOpenAI(systemPrompt, extractedText);
      if (typeof parsedData === 'string') {
        parsedData = JSON.parse(parsedData);
      }

      setFormData(prev => ({
        ...prev,
        name: parsedData.name || prev.name,
        title: parsedData.title || prev.title,
        summary: parsedData.summary || prev.summary,
        skills: parsedData.skills || prev.skills,
        education: parsedData.education || prev.education,
        experience: parsedData.experience || prev.experience,
        publications: parsedData.publications || prev.publications,
        gpa: parsedData.gpa || prev.gpa,
        affiliations: parsedData.affiliations || prev.affiliations,
        certifications: parsedData.certifications || prev.certifications,
        organizations: parsedData.organizations || prev.organizations
      }));

      setMessage({ type: 'success', text: 'Resume parsed successfully! Please review and save your changes.' });
    } catch (error) {
      console.error('Error parsing file:', error);
      setMessage({ type: 'error', text: `Failed to parse resume: ${error.message}` });
    } finally {
      setIsParsing(false);
      // Reset the file input so the same file can be uploaded again if needed
      e.target.value = null;
    }
  };


  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file && ['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result;
        const { error } = await supabase.from('profiles').update({ avatar_url: base64String }).eq('id', session.user.id);
        if (!error) {
          refreshData();
          setMessage({ type: 'success', text: 'Profile image updated!' });
        } else {
          setMessage({ type: 'error', text: 'Failed to update profile image.' });
        }
      };
      reader.readAsDataURL(file);
    } else {
      setMessage({ type: 'error', text: 'Please upload a valid image file (JPG, PNG, GIF).' });
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!session?.user?.id) return;

    setIsSaving(true);
    setMessage({ type: '', text: '' });

    let parsedEducation = formData.education;
    if (typeof formData.education === 'string') {
      try {
        parsedEducation = JSON.parse(formData.education);
      } catch (err) {
        setMessage({ type: 'error', text: 'Invalid JSON format for education.' });
        setIsSaving(false);
        return;
      }
    }

    const toArray = (val) => {
      if (Array.isArray(val)) return val;
      if (typeof val === 'string') return val.split(',').map(s => s.trim()).filter(Boolean);
      return [];
    };

    try {
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          name: formData.name,
          title: formData.title,
          summary: formData.summary,
          skills: toArray(formData.skills),
          education: parsedEducation,
          publications: toArray(formData.publications),
          gpa: formData.gpa,
          affiliations: toArray(formData.affiliations),
          certifications: toArray(formData.certifications),
          organizations: toArray(formData.organizations),
          github_url: formData.github_url,
          portfolio_url: formData.portfolio_url,
          social_links: formData.social_links,
          updated_at: new Date().toISOString()
        })
        .eq('id', session.user.id);

      if (profileError) throw profileError;

      if (formData.experience && formData.experience.length > 0) {
        // First delete existing experiences
        const { error: deleteError } = await supabase
          .from('experiences')
          .delete()
          .eq('profile_id', session.user.id);

        if (deleteError) throw deleteError;

        // Then insert new experiences
        const experiencesToInsert = formData.experience.map(exp => ({
          profile_id: session.user.id,
          role: exp.role || 'Unknown Role',
          company: exp.company || 'Unknown Company',
          duration: exp.duration || 'Unknown Duration',
          bullets: exp.bullets || []
        }));

        const { error: insertError } = await supabase
          .from('experiences')
          .insert(experiencesToInsert);

        if (insertError) throw insertError;
      }

      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      refreshData();
    } catch (error) {
      console.error('Error updating profile:', error);
      setMessage({ type: 'error', text: 'Failed to update profile. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-8 flex justify-center items-center">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8 max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-gray-900">Profile Settings</h1>


      <div className="bg-white rounded-xl p-6 shadow mb-8">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2"><SafeIcon icon={FiUser} /> Profile Image</h2>
        <div className="flex items-center gap-6">
          {data?.user?.avatar_url ? (
            <img src={data.user.avatar_url} alt="Profile" className="w-24 h-24 rounded-full object-cover shadow" />
          ) : (
            <div className={`w-24 h-24 rounded-full bg-gradient-to-tr ${data?.user?.gradient || 'from-gray-200 to-gray-300'} flex items-center justify-center text-white text-3xl font-bold shadow`}>
              {data?.user?.initials || 'U'}
            </div>
          )}
          <div>
            <label className="bg-blue-600 text-white px-4 py-2 rounded-xl text-sm font-bold hover:bg-blue-700 flex items-center gap-2 cursor-pointer transition-colors w-fit">
              <SafeIcon icon={FiUploadCloud} /> Upload Image
              <input type="file" accept="image/png, image/jpeg, image/gif" className="hidden" onChange={handleImageUpload} />
            </label>
            <p className="text-xs text-gray-500 mt-2">Recommended: 256x256px. Accepts JPG, PNG, GIF.</p>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-xl mb-8">
        <h2 className="text-xl font-bold mb-4 text-white">Import from Resume</h2>
        <p className="text-gray-400 mb-4">Upload your PDF or DOCX resume to automatically fill out your profile information.</p>

        <div className="flex items-center gap-4">
          <input
            type="file"
            accept=".pdf,.doc,.docx"
            id="resume-upload"
            className="hidden"
            onChange={handleFileUpload}
            disabled={isParsing}
          />
          <label
            htmlFor="resume-upload"
            className={`px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg cursor-pointer transition-colors border border-gray-600 ${isParsing ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {isParsing ? 'Parsing Document...' : 'Upload Resume'}
          </label>
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl p-6 shadow-xl">
        {message.text && (
          <div className={`p-4 rounded-lg mb-6 ${message.type === 'success' ? 'bg-green-600/20 text-green-400 border border-green-600/50' : 'bg-red-600/20 text-red-400 border border-red-600/50'}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
              Full Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              required
            />
          </div>

          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-400 mb-2">
              Professional Title
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="summary" className="block text-sm font-medium text-gray-400 mb-2">
              Professional Summary
            </label>
            <textarea
              id="summary"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              rows={4}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="skills" className="block text-sm font-medium text-gray-400 mb-2">
              Skills (comma-separated)
            </label>
            <input
              type="text"
              id="skills"
              name="skills"
              value={Array.isArray(formData.skills) ? formData.skills.join(', ') : formData.skills}
              onChange={(e) => setFormData(prev => ({ ...prev, skills: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="education" className="block text-sm font-medium text-gray-400 mb-2">
              Education (JSON format)
            </label>
            <textarea
              id="education"
              name="education"
              value={typeof formData.education === 'string' ? formData.education : JSON.stringify(formData.education || [], null, 2)}
              onChange={(e) => setFormData(prev => ({ ...prev, education: e.target.value }))}
              rows={4}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors font-mono text-sm"
            />
          </div>

          <div>
            <label htmlFor="gpa" className="block text-sm font-medium text-gray-400 mb-2">
              GPA
            </label>
            <input
              type="text"
              id="gpa"
              name="gpa"
              value={formData.gpa}
              onChange={handleChange}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="publications" className="block text-sm font-medium text-gray-400 mb-2">
              Publications (comma-separated)
            </label>
            <input
              type="text"
              id="publications"
              name="publications"
              value={Array.isArray(formData.publications) ? formData.publications.join(', ') : formData.publications}
              onChange={(e) => setFormData(prev => ({ ...prev, publications: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="affiliations" className="block text-sm font-medium text-gray-400 mb-2">
              Affiliations (comma-separated)
            </label>
            <input
              type="text"
              id="affiliations"
              name="affiliations"
              value={Array.isArray(formData.affiliations) ? formData.affiliations.join(', ') : formData.affiliations}
              onChange={(e) => setFormData(prev => ({ ...prev, affiliations: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="certifications" className="block text-sm font-medium text-gray-400 mb-2">
              Certifications (comma-separated)
            </label>
            <input
              type="text"
              id="certifications"
              name="certifications"
              value={Array.isArray(formData.certifications) ? formData.certifications.join(', ') : formData.certifications}
              onChange={(e) => setFormData(prev => ({ ...prev, certifications: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div>
            <label htmlFor="organizations" className="block text-sm font-medium text-gray-400 mb-2">
              Organizations (comma-separated)
            </label>
            <input
              type="text"
              id="organizations"
              name="organizations"
              value={Array.isArray(formData.organizations) ? formData.organizations.join(', ') : formData.organizations}
              onChange={(e) => setFormData(prev => ({ ...prev, organizations: e.target.value }))}
              className="w-full bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
            />
          </div>

          <div className="space-y-4 pt-4 border-t border-gray-800">
            <label className="text-sm font-bold text-gray-400">Web Presence & Social Links</label>
            {formData.social_links.map((link, idx) => (
              <div key={idx} className="flex gap-4">
                <select
                  value={link.platform}
                  onChange={(e) => handleSocialLinkChange(idx, 'platform', e.target.value)}
                  className="w-1/3 bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                >
                  <option value="LinkedIn">LinkedIn</option>
                  <option value="GitHub">GitHub</option>
                  <option value="Portfolio">Portfolio</option>
                  <option value="Dribbble">Dribbble</option>
                  <option value="Behance">Behance</option>
                  <option value="Twitter">Twitter</option>
                  <option value="Website">Website</option>
                </select>
                <input
                  type="text"
                  placeholder="https://..."
                  value={link.url}
                  onChange={(e) => handleSocialLinkChange(idx, 'url', e.target.value)}
                  className="w-2/3 bg-gray-900 border border-gray-700 rounded-lg p-3 text-white focus:outline-none focus:border-blue-500"
                />
              </div>
            ))}
          </div>

          <div className="pt-4">
            <button
              type="submit"
              disabled={isSaving}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-600/50 disabled:cursor-not-allowed text-white font-medium rounded-lg shadow-lg shadow-blue-500/30 transition-all"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Settings;